import { ReactNode, useMemo } from 'react'
import { createContainer } from 'unstated-next'

import { EmbroiderableProductEmbroideryConfig } from '../../../types/graphql/product'
import {
  AnatomyConstraintOverlay,
  EMBROIDERY_TYPES,
  EmbroideryConstraintSet,
  EmbroideryTypeKey,
  RuleCheck,
} from '../combination-rules/types'
import { useEmbroideryDesignsQuery } from '../useEmbroideryDesigns'

import { deriveAnatomy } from './adapters/deriveAnatomy'

export type EmbroideryGarmentProviderProps = {
  /** Defines which embroidery this product offers and where it can go. */
  productEmbroideryConfig: EmbroiderableProductEmbroideryConfig | null | undefined
  /**
   * The garment color's hex codes, straight from the catalog — every call site passes
   * `variantGroup.color?.hexCodes`. The first is the swatch color. Kept as the list rather than
   * the one color so a two-tone swatch stays possible without changing every render site.
   */
  productColorHexCodes?: readonly string[] | null
  /** The flow's rulebook, such as `dtcEmbroideryRules`. */
  rules: readonly RuleCheck[]
  /** Narrows the garment's anatomy from runtime data, such as a team store's allowed placements. */
  anatomyOverlay?: AnatomyConstraintOverlay
  /** Required embroidery for this garment, optional and mainly used for B2B */
  requiredTypes?: readonly EmbroideryTypeKey[]
}

/**
 * Everything about this garment the customer cannot change: which types of embroidery the catalog
 * offers on it and where each can go, the character and line limits, the garment's color, and the
 * fonts, thread colors and icon designs the forms let them choose from.
 *
 * It also assembles the engine's `constraints` — this garment's anatomy plus the flow's rulebook —
 * so selection and availability cannot each build their own and drift apart.
 */
function useEmbroideryGarmentImpl(props?: EmbroideryGarmentProviderProps) {
  // unstated-next types `initialState` as optional; the exported provider below is what makes it
  // required, so by the time this runs there is always one.
  const { productEmbroideryConfig, productColorHexCodes, rules, anatomyOverlay, requiredTypes } =
    props!

  const {
    data: designsData,
    previousData: previousDesignsData,
    status: designsStatus,
  } = useEmbroideryDesignsQuery()

  const constraints = useMemo<EmbroideryConstraintSet>(
    () => ({
      rules,
      anatomy: deriveAnatomy(productEmbroideryConfig),
      anatomyOverlay,
      requiredTypes,
    }),
    [rules, productEmbroideryConfig, anatomyOverlay, requiredTypes]
  )

  const designs = useMemo(() => {
    const options = (designsData ?? previousDesignsData)?.embroideryOptions
    return {
      fonts: options?.fonts ?? [],
      threadColors: options?.colors ?? [],
      iconGroups: options?.iconGroups ?? [],
    }
  }, [designsData, previousDesignsData])

  /**
   * The types the catalog declares for this product. This is config presence only — it is what
   * picks out the priced embroidery variants. Whether a type is actually offered to the customer,
   * once the anatomy overlay has had its say, is the availability matrix's answer, not this.
   */
  const catalogTypeKeys = useMemo(
    () => EMBROIDERY_TYPES.filter(typeKey => !!productEmbroideryConfig?.options[typeKey]),
    [productEmbroideryConfig]
  )

  return {
    constraints,
    /**
     * The raw catalog config, for the character and line limits and the font and color
     * restrictions. Its `options[type].placements` is the catalog's own list, before the flow's
     * rules or any overlay narrow it — for where the customer can actually put something, ask the
     * availability provider rather than reading it from here.
     */
    productEmbroideryConfig,
    productColorHex: productColorHexCodes?.[0],
    designs,
    designsStatus,
    catalogTypeKeys,
  }
}

const EmbroideryGarmentContainer = createContainer(useEmbroideryGarmentImpl)

/**
 * Wrapped rather than exported straight from the container, so `initialState` is required. Every
 * garment fact the stack has comes through it, and a tree mounted without one would fail on the
 * first read instead of at the mount site that forgot it.
 */
export const EmbroideryGarmentProvider = ({
  initialState,
  children,
}: {
  initialState: EmbroideryGarmentProviderProps
  children?: ReactNode
}) => (
  <EmbroideryGarmentContainer.Provider initialState={initialState}>
    {children}
  </EmbroideryGarmentContainer.Provider>
)

export const useEmbroideryGarment = EmbroideryGarmentContainer.useContainer
